import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Badge } from "../ui/badge";
import { MessageSquare, FileText, Receipt, Banknote, Clock } from "lucide-react";

const API = `${window.location.origin}/api`;

const ICON = {
  enquiry: MessageSquare,
  quote: FileText,
  invoice: Receipt,
  payment: Banknote,
};

const TYPE_COLOR = {
  enquiry: "bg-blue-100 text-blue-800 border-blue-200",
  quote: "bg-purple-100 text-purple-800 border-purple-200",
  invoice: "bg-amber-100 text-amber-800 border-amber-200",
  payment: "bg-green-100 text-green-800 border-green-200",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch (e) {
    return iso;
  }
}

export const CustomerTimeline = ({ authHeaders, customerId, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTimeline = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/customers/${customerId}/timeline`, authHeaders);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, customerId]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  if (loading && !data) return <div className="text-xs text-muted-foreground py-2">Loading timeline…</div>;
  if (!data) return null;

  const { events = [], totals = {} } = data;

  return (
    <div className="space-y-3" data-testid="customer-timeline">
      <div className="flex items-center gap-2 font-medium text-sm">
        <Clock className="h-4 w-4" />
        Customer journey
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div className="rounded border p-2 text-center">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Enquiries</div>
          <div className="font-semibold text-sm" data-testid="timeline-count-enquiries">{totals.enquiries || 0}</div>
        </div>
        <div className="rounded border p-2 text-center">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Quotes</div>
          <div className="font-semibold text-sm" data-testid="timeline-count-quotes">{totals.quotes || 0}</div>
        </div>
        <div className="rounded border p-2 text-center">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Invoices</div>
          <div className="font-semibold text-sm" data-testid="timeline-count-invoices">{totals.invoices || 0}</div>
        </div>
        <div className="rounded border p-2 text-center">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Payments</div>
          <div className="font-semibold text-sm" data-testid="timeline-count-payments">{totals.payments || 0}</div>
        </div>
        <div className="rounded border p-2 text-center col-span-2 sm:col-span-1 bg-green-50">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Lifetime paid</div>
          <div className="font-semibold text-sm text-green-700" data-testid="timeline-lifetime-value">£{(totals.lifetime_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 text-center border rounded bg-muted/20">No activity yet for this customer.</div>
      ) : (
        <div className="relative pl-4 border-l-2 border-muted space-y-2 max-h-80 overflow-y-auto">
          {events.map((ev, idx) => {
            const Icon = ICON[ev.type] || Clock;
            const typeClass = TYPE_COLOR[ev.type] || "bg-gray-100 text-gray-800";
            return (
              <button
                key={`${ev.type}-${ev.record_id}-${idx}`}
                type="button"
                onClick={() => onNavigate && onNavigate(ev.type, ev.record_id)}
                className="relative block w-full text-left rounded border bg-white hover:bg-muted/30 transition p-2 pl-3"
                data-testid={`timeline-event-${ev.type}-${ev.record_id}`}
              >
                <span className={`absolute -left-[22px] top-3 w-4 h-4 rounded-full flex items-center justify-center ${typeClass}`}>
                  <Icon className="h-2.5 w-2.5" />
                </span>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Badge variant="outline" className={`${typeClass} text-[10px]`}>{ev.type}</Badge>
                    <span className="font-medium truncate">{ev.title}</span>
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(ev.date)}</div>
                </div>
                {(ev.subtitle || ev.value || ev.status) && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    {ev.subtitle && <span className="truncate flex-1">{ev.subtitle}</span>}
                    {ev.status && <Badge variant="outline" className="text-[9px]">{ev.status}</Badge>}
                    {ev.value > 0 && <span className="font-semibold">£{ev.value.toFixed(2)}</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerTimeline;
